// 로컬 캐릭터 목록(dungeonhero.characters.v1, 계정 단위·기기 전용 사본 — 정본은 서버 /api/characters).
// 담는 것: 씨앗 · 지금 캐릭터 · 캐릭터 줄(서버 등록 여부·계정별 충돌) · 이관 표시(이 기기 원래 키가 어떤 계정·계획으로 처리됐는지) · 진행 중 이관 claim.
// 순수(저장소 주입) — 시험이 MapKV로 돈다.
import type { KV } from '../account/keys';
import { CLASS_IDS, isCharacterId, isClassId, LOOK_COUNT, type CharacterView, type ClassId } from './protocol';
import { parseAllocation, type MigrationAllocation } from './allocation';

export const ROSTER_KEY = 'dungeonhero.characters.v1';
/** 이관 규칙 판(바꾸면 올린다 — 이관 표시에 남는다) */
export const MIGRATION_VERSION = 2;

export interface LocalCharacter {
  id: string;
  cls: ClassId;
  look: number;
  slot: number;
  name: string | null;
  tag: number | null;
  origin: string | null;
  /** 서버에 등록됐는가 */
  registered: boolean;
  /** 등록(소유)을 확인한 계정 id(기기 계정이 바뀌면 다시 확인 — 같은 계정이면 멱등) */
  account?: string;
  /** 서버가 이 id를 그 계정 것이 아니라고 거절한 계정들(그 계정에서만 동기화 멈춤 — 소유가 확인되면 지운다) */
  conflictAccounts?: string[];
  /** 서버에서 삭제 유예 중(목록 동기화로 채운다) */
  deletedAt?: number | null;
  /** 유예가 끝나 영구 삭제되는 시각(삭제 대기일 때) */
  purgeAt?: number | null;
  /** 무료 이름 변경이 남았는가(서버 목록 값 — 화면의 「이름 바꾸기」 표시) */
  renameFree?: boolean;
  /** 이 계정에 칸이 부족해 서버에 올리지 못하고 이 기기에만 보관하는 캐릭터(진행은 로컬에 그대로) */
  localOnly?: boolean;
  /** 마지막으로 본 서버 스냅샷 rev(목록 맞추기·이관·받기에서 채운다) */
  serverRev?: number;
  /** 파생 값(저장하지 않음 — index.ts deriveRosterState가 매번 계산): 로컬에 설치·검증된 rev가 서버 rev와 달라 받기 전까지 선택 불가 */
  needsDownload?: boolean;
}
/**
 * 이 기기 원래 키의 처리 표시(계정당 한 번, 서버 기준 — online.ts). 있으면 다시 이관하지 않는다.
 * mode: first = 이 기기가 계정의 첫 이관 · adopt = 계정에 이미 있는 캐릭터를 받음 · none = 원래 진행이 없었다
 */
export interface MigrationMark {
  v: number; at: number; mode: 'first' | 'adopt' | 'none';
  /** 처리한 계정과 계획 id(= 계획 해시, 없으면 null) */
  account: string; hash: string | null;
  allocation: MigrationAllocation | null;
  /** 이 기기가 만든 캐릭터(first) */
  ids: string[];
  higher: string | null;
  /** 처리 때 원래 진행 스냅샷 해시 — 그 뒤 원래 키에 진행이 더 쌓였는지(옛 빌드) 안내용 */
  legacyHash: string | null;
}
export interface PendingMigration {
  account: string; claim: string; hash: string; allocation: MigrationAllocation;
  ids: string[]; origins: Record<string, 'legacy-kael' | 'legacy-lyra'>; classes: Record<string, ClassId>; higher: string; legacyHash: string | null;
}
export interface Roster {
  v: 1;
  seed: string;
  current: string | null;
  characters: LocalCharacter[];
  migrated: MigrationMark | null;
  /**
   * 이관 작업 기록(첫 시도 때 고정): claim과 계획(해시·배분·캐릭터 id·높은 캐릭터)·처리 시작 때 원래 진행 해시. 캐릭터 스냅샷은 그때 캐릭터 키에 써 둔 값이다.
   * 재시도는 다시 계산하지 않고 이 계획·스냅샷을 그대로 이어 올린다(그 사이 레벨·장비가 바뀌어도 해시가 같다 — Codex P2 #214)
   */
  pendingMigration?: PendingMigration;
  /** 응답을 못 받은 생성 요청(같은 내용으로 다시 누르면 같은 id로 보내 서버가 멱등으로 돌려준다 — 슬롯을 두 번 쓰지 않게) */
  pendingCreate?: { id: string; cls: ClassId; look: number; name: string };
}

const obj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
function parseChar(v: unknown): LocalCharacter | null {
  if (!obj(v) || !isCharacterId(v.id) || !isClassId(v.cls)) return null;
  const look = Number.isInteger(v.look) && (v.look as number) >= 0 && (v.look as number) < LOOK_COUNT ? v.look as number : 0;
  const conflicts = strs(v.conflictAccounts);
  return {
    id: v.id, cls: v.cls, look, slot: Number.isInteger(v.slot) ? v.slot as number : 0,
    name: typeof v.name === 'string' ? v.name : null, tag: Number.isInteger(v.tag) ? v.tag as number : null,
    origin: typeof v.origin === 'string' ? v.origin : null, registered: v.registered === true,
    ...(typeof v.account === 'string' ? { account: v.account } : {}),
    ...(conflicts.length ? { conflictAccounts: conflicts } : {}),
    ...(typeof v.deletedAt === 'number' ? { deletedAt: v.deletedAt } : {}),
    // 삭제 유예 종료 시각·무료 이름 변경 여부도 읽을 때 버리지 않는다(화면의 「n일 남음」·「이름 바꾸기」가 새로고침 뒤에도 유지)
    ...(typeof v.purgeAt === 'number' ? { purgeAt: v.purgeAt } : {}),
    ...(typeof v.renameFree === 'boolean' ? { renameFree: v.renameFree } : {}),
    ...(v.localOnly === true ? { localOnly: true } : {}),
    ...(Number.isSafeInteger(v.serverRev) ? { serverRev: v.serverRev as number } : {}),
  };
}
function parseMark(m: unknown): MigrationMark | null {
  if (!obj(m) || typeof m.account !== 'string' || (m.mode !== 'first' && m.mode !== 'adopt' && m.mode !== 'none')) return null;
  return {
    v: Number(m.v) || MIGRATION_VERSION, at: Number(m.at) || 0, mode: m.mode, account: m.account,
    hash: typeof m.hash === 'string' ? m.hash : null, allocation: parseAllocation(m.allocation),
    ids: strs(m.ids).filter(isCharacterId), higher: isCharacterId(m.higher) ? m.higher : null,
    legacyHash: typeof m.legacyHash === 'string' ? m.legacyHash : null,
  };
}

/** 읽기: 없으면 null, 손상이면 'corrupt'(덮어쓰지 않는다 — 부팅은 원래 키로 간다) */
export function loadRoster(kv: KV): Roster | null | 'corrupt' {
  const raw = kv.getItem(ROSTER_KEY);
  if (raw === null) return null;
  try {
    const p: unknown = JSON.parse(raw);
    if (!obj(p) || p.v !== 1 || typeof p.seed !== 'string' || !Array.isArray(p.characters)) return 'corrupt';
    const characters = p.characters.map(parseChar).filter((c): c is LocalCharacter => c !== null);
    const current = isCharacterId(p.current) && characters.some((c) => c.id === p.current) ? p.current : null;
    const pm = p.pendingMigration;
    const pmAlloc = obj(pm) ? parseAllocation(pm.allocation) : null;
    const pendingMigration: PendingMigration | undefined = obj(pm) && pmAlloc && typeof pm.account === 'string' && typeof pm.claim === 'string' && typeof pm.hash === 'string' && isCharacterId(pm.higher)
      && obj(pm.origins) && obj(pm.classes) && strs(pm.ids).every(isCharacterId)
      ? { account: pm.account, claim: pm.claim, hash: pm.hash, allocation: pmAlloc, ids: strs(pm.ids), origins: pm.origins as PendingMigration['origins'], classes: pm.classes as PendingMigration['classes'], higher: pm.higher, legacyHash: typeof pm.legacyHash === 'string' ? pm.legacyHash : null }
      : undefined;
    const pc = p.pendingCreate;
    const pendingCreate = obj(pc) && isCharacterId(pc.id) && isClassId(pc.cls) && typeof pc.name === 'string' && Number.isInteger(pc.look) ? { id: pc.id, cls: pc.cls, look: pc.look as number, name: pc.name } : undefined;
    return { v: 1, seed: p.seed, current, characters, migrated: parseMark(p.migrated), ...(pendingMigration ? { pendingMigration } : {}), ...(pendingCreate ? { pendingCreate } : {}) };
  } catch { return 'corrupt'; }
}

/** 쓰기 + 되읽기 확인(실패면 false) */
export function saveRoster(kv: KV, r: Roster): boolean {
  // 파생 값(needsDownload)은 저장하지 않는다 — 읽을 때마다 deriveRosterState가 계산한다
  const text = JSON.stringify({ ...r, characters: r.characters.map(({ needsDownload: _n, ...c }) => c) });
  try { kv.setItem(ROSTER_KEY, text); return kv.getItem(ROSTER_KEY) === text; } catch { return false; }
}

export function freshRoster(seed: string): Roster { return { v: 1, seed, current: null, characters: [], migrated: null }; }

/** 서버 캐릭터 줄 → 로컬 줄 */
export function fromView(v: CharacterView, account?: string): LocalCharacter {
  return { id: v.id, cls: v.cls, look: v.look, slot: v.slot, name: v.name, tag: v.tag, origin: v.origin, registered: true, deletedAt: v.deletedAt, purgeAt: v.purgeAt ?? null, renameFree: v.renameFree, serverRev: v.rev ?? 0, ...(account ? { account } : {}) };
}

/** 서버 목록으로 로컬 줄을 맞춘다: 서버에 있는 것은 이름·태그·슬롯·삭제 유예를 서버 값으로, 등록 안 된 로컬 줄은 남긴다 */
export function mergeServerList(r: Roster, server: readonly CharacterView[], account?: string): Roster {
  const byId = new Map(server.map((s) => [s.id, s]));
  const characters: LocalCharacter[] = [];
  for (const c of r.characters) {
    const s = byId.get(c.id);
    if (s) {
      const { conflictAccounts, ...rest } = c;
      const left = account ? (conflictAccounts ?? []).filter((x) => x !== account) : conflictAccounts ?? [];
      characters.push({ ...rest, ...fromView(s), ...(account ? { account } : c.account ? { account: c.account } : {}), ...(left.length ? { conflictAccounts: left } : {}) });
      byId.delete(c.id);
    } else if (!c.registered) characters.push(c);
    // 등록됐는데 서버에 없다 = 영구 삭제됨 → 목록에서 뺀다(로컬 진행 키는 지우지 않는다)
  }
  // 처음 보는 서버 캐릭터: 로컬 설치 기준이 없으니 deriveRosterState가 「받는 중」으로 본다(기본값이 서버 진행을 덮지 않게 — Codex P2 #214)
  for (const s of byId.values()) characters.push(fromView(s, account));
  characters.sort((a, b) => a.slot - b.slot);
  // 지금 캐릭터가 서버에서 삭제 대기면 선택을 비운다(다음 부팅은 선택 화면 — Codex P2 #214)
  const current = r.current && characters.some((c) => c.id === r.current && !c.deletedAt) ? r.current : null;
  return { ...r, characters, current };
}

export { CLASS_IDS };
